import { EarningSummaryItem } from '@app/models/EarningSummaryItem'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IProjectedEarningsState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: any
  readonly totalItems: number
}

const ProjectedEarningsStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: [],
  totalItems: 0,
})

export class ProjectedEarningsState extends ProjectedEarningsStateRecord
  implements IProjectedEarningsState {
  public static fromJS(initialState?: Partial<IProjectedEarningsState>) {
    if (!initialState) {
      return new ProjectedEarningsState()
    }

    const { items, ...rest } = initialState
    return new ProjectedEarningsState({
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: any
  public readonly totalItems: number

  constructor(props?: Partial<ProjectedEarningsState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ProjectedEarningsActionTypes {
  LoadProjectedEarnings = 'PROJECTED_EARNINGS_REPORT::LOAD_PROJECTED_EARNINGS',
  LoadProjectedEarningsFailure = 'PROJECTED_EARNINGS_REPORT::LOAD_PROJECTED_EARNINGS_FAILURE',
  LoadProjectedEarningsRequest = 'PROJECTED_EARNINGS_REPORT::LOAD_PROJECTED_EARNINGS_REQUEST',
  LoadProjectedEarningsSuccess = 'PROJECTED_EARNINGS_REPORT::LOAD_PROJECTED_EARNINGS_SUCCESS',
  SetFilterText = 'PROJECTED_EARNINGS_REPORT::SET_FILTER_TEXT',
}

interface ILoadProjectedEarningsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ProjectedEarningsActionTypes.LoadProjectedEarnings
}

interface ILoadProjectedEarningsFailureAction {
  error: string
  type: ProjectedEarningsActionTypes.LoadProjectedEarningsFailure
}

interface ILoadProjectedEarningsRequestAction {
  type: ProjectedEarningsActionTypes.LoadProjectedEarningsRequest
}

interface ILoadProjectedEarningsSuccessAction {
  append: boolean
  items: EarningSummaryItem[]
  totalItems: number
  type: ProjectedEarningsActionTypes.LoadProjectedEarningsSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: ProjectedEarningsActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadProjectedEarningsFailureAction
  | ILoadProjectedEarningsRequestAction
  | ILoadProjectedEarningsSuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadProjectedEarnings(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadProjectedEarningsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ProjectedEarningsActionTypes.LoadProjectedEarnings,
  }
}

function loadProjectedEarningsFailure(
  error: string,
): ILoadProjectedEarningsFailureAction {
  return {
    error,
    type: ProjectedEarningsActionTypes.LoadProjectedEarningsFailure,
  }
}

function loadProjectedEarningsRequest(): ILoadProjectedEarningsRequestAction {
  return {
    type: ProjectedEarningsActionTypes.LoadProjectedEarningsRequest,
  }
}

function loadProjectedEarningsSuccess(
  items: EarningSummaryItem[],
  append: boolean,
  totalItems: number,
): ILoadProjectedEarningsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ProjectedEarningsActionTypes.LoadProjectedEarningsSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ProjectedEarningsActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadProjectedEarnings,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ProjectedEarningsState> = (
  state: ProjectedEarningsState = new ProjectedEarningsState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ProjectedEarningsActionTypes.LoadProjectedEarningsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ProjectedEarningsState

    case ProjectedEarningsActionTypes.LoadProjectedEarningsRequest:
      return state.set('isLoading', true) as ProjectedEarningsState

    case ProjectedEarningsActionTypes.LoadProjectedEarningsSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', action.items)
        .set('totalItems', action.totalItems) as ProjectedEarningsState

    case ProjectedEarningsActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as ProjectedEarningsState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadProjectedEarningsSaga(action: ILoadProjectedEarningsAction) {
  try {
    yield put(loadProjectedEarningsRequest())

    const currentState: ProjectedEarningsState = yield select(
      (state: ApplicationState) => state.projectedEarnings,
    )

    const query: string[] = []

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/BrokeragePipeLines/undefined/Earnings'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: EarningSummaryItem[] = result.results.map((c: any) =>
        EarningSummaryItem.fromJS(c),
      )
      yield put(loadProjectedEarningsSuccess(contacts, false, result.count))
    } else {
      yield put(loadProjectedEarningsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadProjectedEarningsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadProjectedEarnings() {
  yield takeLatest(
    ProjectedEarningsActionTypes.LoadProjectedEarnings,
    loadProjectedEarningsSaga,
  )
}

export function* projectedEarningsRootSaga() {
  yield fork(watchLoadProjectedEarnings)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ProjectedEarningsState => state.projectedEarnings

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
